import { ReactNode } from 'react';
import { useTranslate } from 'react-admin';
import {
  Box,
  Button,
  Card,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { useResponsive } from 'src/hooks/use-responsive';
import { PermissionButton } from 'src/routes/components/permission-button';

import { fieldUnits, formatValue } from '../params/helpers';
import { VAULTS_NS, VAULTS_PERMISSION_RESOURCE } from '../vaults/constants';
import { formatInstant, formatUsdcAmount } from '../vaults/helpers';
import { VaultAddress } from '../vaults/VaultAddress';
import { OFFICIAL_VAULTS_NS as NS } from './constants';
import { ApplicationView, canEditApplicationContact, requesterLabel } from './helpers';
import { KindChip, StatusChip } from './StatusChip';

/** The PO's columns (2026-10-01). Each label names both kinds: "Vault ID / Application ID", … */
const COLUMNS = [
  'vaultId',
  'name',
  'manager',
  'stake',
  'lockupDays',
  'feeRate',
  'ownershipFloor',
  'status',
  'requestedAt',
  'requestedBy',
  'actions',
];
const NUMERIC = { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' } as const;
const ID = { fontFamily: 'monospace', fontSize: 12, overflowWrap: 'anywhere' } as const;
const EMPTY = '—';
/** Actions stays in view while the wide table scrolls sideways. */
const PINNED = {
  position: 'sticky',
  right: 0,
  bgcolor: 'background.paper',
  boxShadow: (theme: { palette: { divider: string } }) => `inset 1px 0 0 ${theme.palette.divider}`,
} as const;

const Manager = ({ request }: { request: ApplicationView }) =>
  request.manager ? (
    <Box sx={ID}>
      <div>{request.manager.holderId}</div>
      <Box sx={{ color: 'text.secondary' }}>{request.manager.address}</Box>
    </Box>
  ) : (
    <>{EMPTY}</>
  );

/** The stake as typed, grouped (PO, 2026-10-01): "1,000 USDC". */
const stakeOf = (r: ApplicationView) =>
  r.stake ? `${formatUsdcAmount(r.stake.expected)} USDC` : EMPTY;

export const RequestsTable = ({
  requests,
  onViewDetails,
  onEditContact,
}: {
  requests: ApplicationView[];
  onViewDetails: (vaultId: string) => void;
  /** XBIT-4401: the contact edit, offered where canEditApplicationContact says so. */
  onEditContact?: (request: ApplicationView) => void;
}) => {
  const translate = useTranslate();
  const smDown = useResponsive('down', 'sm');
  const units = fieldUnits(translate);

  const failure = (r: ApplicationView) =>
    r.status === 'FAILED' && r.failure
      ? translate(`${NS}.table.failure`, { step: r.failure.step, reason: r.failure.reason })
      : null;

  const viewDetails = (r: ApplicationView) => (
    <Button
      size="small"
      variant="outlined"
      sx={{ whiteSpace: 'nowrap' }}
      onClick={() => onViewDetails(r.vaultId)}
    >
      {translate(`${NS}.table.viewDetails`)}
    </Button>
  );

  // XBIT-4401: Vault Management's permission, since the edit is the vault's (POST /vault/vaults/:id/contact).
  const editContact = (r: ApplicationView) =>
    onEditContact && canEditApplicationContact(r) ? (
      <PermissionButton resource={VAULTS_PERMISSION_RESOURCE} action="update">
        <Button
          size="small"
          variant="outlined"
          sx={{ whiteSpace: 'nowrap' }}
          onClick={() => onEditContact(r)}
        >
          {translate(`${VAULTS_NS}.contact.edit`)}
        </Button>
      </PermissionButton>
    ) : null;

  const actions = (r: ApplicationView) => (
    <Stack direction="row" spacing={1}>
      {viewDetails(r)}
      {editContact(r)}
    </Stack>
  );

  if (requests.length === 0) {
    return (
      <Card sx={{ p: 3 }}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {translate(`${NS}.empty`)}
        </Typography>
      </Card>
    );
  }

  if (smDown) {
    return (
      <Stack spacing={2}>
        {requests.map((r) => (
          <Card key={r.vaultId} sx={{ p: 2 }}>
            <Stack
              direction="row"
              spacing={1}
              sx={{ justifyContent: 'space-between', alignItems: 'flex-start' }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                  <Typography variant="subtitle1" sx={{ overflowWrap: 'anywhere' }}>
                    {r.name}
                  </Typography>
                  <KindChip kind={r.kind} />
                </Stack>
                <Typography variant="caption" sx={{ ...ID, color: 'text.secondary' }}>
                  {r.vaultId}
                </Typography>
                <VaultAddress address={r.hlAddress} />
              </Box>
              <StatusChip request={r} />
            </Stack>
            <Box
              component="dl"
              sx={{
                m: 0,
                mt: 1.5,
                display: 'grid',
                gap: 1.5,
                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
              }}
            >
              {(
                [
                  ['manager', <Manager request={r} />],
                  ['stake', stakeOf(r)],
                  ['lockupDays', formatValue('days', r.lockupDays, units)],
                  ['feeRate', formatValue('percent', r.feeRate, units)],
                  ['ownershipFloor', formatValue('percent', r.ownershipFloor, units)],
                  ['requestedAt', formatInstant(r.requestedAt)],
                  ['requestedBy', requesterLabel(r)],
                ] as [string, ReactNode][]
              ).map(([key, value]) => (
                <Box key={key} sx={{ minWidth: 0 }}>
                  <Typography component="dt" variant="caption" sx={{ color: 'text.secondary' }}>
                    {translate(`${NS}.table.${key}`)}
                  </Typography>
                  <Typography
                    component="dd"
                    variant="body2"
                    sx={{ m: 0, overflowWrap: 'anywhere' }}
                  >
                    {value}
                  </Typography>
                </Box>
              ))}
            </Box>
            {failure(r) && (
              <Typography
                variant="body2"
                sx={{ mt: 1.5, color: 'error.main', overflowWrap: 'anywhere' }}
              >
                {failure(r)}
              </Typography>
            )}
            <Box sx={{ mt: 1.5 }}>{actions(r)}</Box>
          </Card>
        ))}
      </Stack>
    );
  }

  return (
    <Card>
      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 1280 }}>
          <TableHead>
            <TableRow>
              {COLUMNS.map((c) => (
                <TableCell key={c} sx={c === 'actions' ? PINNED : undefined}>
                  {translate(`${NS}.table.${c}`)}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {requests.map((r) => (
              <TableRow key={r.vaultId}>
                <TableCell sx={ID}>
                  <div>{r.vaultId}</div>
                  <VaultAddress address={r.hlAddress} />
                </TableCell>
                <TableCell>
                  <Stack spacing={0.5} sx={{ alignItems: 'flex-start' }}>
                    <span>{r.name}</span>
                    <KindChip kind={r.kind} />
                  </Stack>
                </TableCell>
                <TableCell sx={{ maxWidth: 220 }}>
                  <Manager request={r} />
                </TableCell>
                <TableCell sx={NUMERIC}>{stakeOf(r)}</TableCell>
                <TableCell sx={NUMERIC}>{formatValue('days', r.lockupDays, units)}</TableCell>
                <TableCell sx={NUMERIC}>{formatValue('percent', r.feeRate, units)}</TableCell>
                <TableCell sx={NUMERIC}>
                  {formatValue('percent', r.ownershipFloor, units)}
                </TableCell>
                <TableCell>
                  <StatusChip request={r} />
                  {failure(r) && (
                    <Typography
                      variant="caption"
                      component="div"
                      sx={{ mt: 0.5, maxWidth: 280, color: 'error.main', overflowWrap: 'anywhere' }}
                    >
                      {failure(r)}
                    </Typography>
                  )}
                </TableCell>
                <TableCell sx={NUMERIC}>{formatInstant(r.requestedAt)}</TableCell>
                <TableCell sx={r.requestedByName ? { overflowWrap: 'anywhere' } : ID}>
                  {requesterLabel(r)}
                </TableCell>
                <TableCell sx={PINNED}>{actions(r)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Card>
  );
};
